import { Select, type SelectProps, type ComboboxItem } from '@mantine/core';
import { useMemo } from 'react';
import { DateTime, IANAZone } from 'ts-luxon';

interface TimeZoneInputProps extends Omit<SelectProps, 'data' | 'value' | 'onChange'> {
    value?: string; // Timezone name (e.g., 'America/New_York')
    onChange?: (value: string | null) => void; // Timezone name
}

interface TimeZoneItem extends ComboboxItem {
    name: string;
    offset: number;
    isCurrent: boolean;
}

export const TimeZoneInput = ({
    value,
    defaultValue,
    onChange,
    placeholder = 'Select a timezone',
    searchable = true,
    clearable = true,
    ...selectProps
}: TimeZoneInputProps) => {
    const timezones = useMemo((): ComboboxItem[] => {
        const now = DateTime.now();
        const currentZone = now.zone;

        return Intl.supportedValuesOf('timeZone')
            .reduce((acc, tzName) => {
                const zone = IANAZone.create(tzName);

                if (!zone.isValid) return acc;

                const dtInZone = now.setZone(zone);
                const offsetMinutes = zone.offset(now.toMillis());

                // Get a more readable timezone name
                const tzNameParts = tzName.split('/');
                const displayName =
                    tzNameParts.length > 1
                        ? tzNameParts.slice(1).join(' / ').replace(/_/g, ' ')
                        : tzName.replace(/_/g, ' ');

                acc.push({
                    value: tzName,
                    label: `(UTC${dtInZone.toFormat('ZZ')}) ${displayName} `,
                    name: displayName,
                    offset: offsetMinutes,
                    isCurrent: zone.equals(currentZone),
                });
                return acc;
            }, [] as TimeZoneItem[])
            .sort((a, b) => {
                // Sort by offset first, then by name
                if (a.offset !== b.offset) {
                    return a.offset - b.offset;
                }
                return a.name.localeCompare(b.name);
            });
    }, []);

    const handleChange = (selectedValue: string | null) => {
        onChange?.(selectedValue);
    };

    return (
        <Select
            data={timezones}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            placeholder={placeholder}
            searchable={searchable}
            clearable={clearable}
            maxDropdownHeight={300}
            {...selectProps}
        />
    );
};
